import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, ExternalLink, LayoutTemplate } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { BlockEditor } from '@/components/block-editor/block-editor'
import { usePageBySlug, useUpdatePageSections, useCreatePage } from '@/hooks/use-pages'
import type { BlockConfig } from '@/hooks/use-homepage'

// Default sections for the member-profile system page
const DEFAULT_MEMBER_PROFILE_BLOCKS: BlockConfig[] = [
  { id: 'member-hero',   component: 'member-hero',   enabled: true, order: 1, settings: {} },
  { id: 'member-info',   component: 'member-info',   enabled: true, order: 2, settings: {} },
  { id: 'member-bio',    component: 'member-bio',    enabled: true, order: 3, settings: {} },
  { id: 'member-social', component: 'member-social', enabled: true, order: 4, settings: {} },
]

// Map of page slugs to their page_scope value for BlockEditor filtering
const PAGE_SCOPE_MAP: Record<string, string> = {
  'member-profile': 'member-detail',
}

export default function PageEditorPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const tenant = useAuthStore((s) => s.currentTenant)
  const { data: page, isLoading } = usePageBySlug(slug ?? '')
  const updateSections = useUpdatePageSections(slug ?? '')
  const createPage = useCreatePage()
  const [saved, setSaved] = useState(false)

  function handleSave(blocks: BlockConfig[]) {
    updateSections.mutate(blocks, {
      onSuccess: () => {
        setSaved(true)
        setTimeout(() => setSaved(false), 3000)
      },
    })
  }

  if (!slug) return null
  if (isLoading) return <p className="text-muted-foreground">Memuat halaman...</p>

  // Member-profile: page doesn't exist yet → offer lazy initialization
  if (!page && slug === 'member-profile') {
    return (
      <div className="space-y-6 max-w-lg">
        <button
          onClick={() => navigate('/pages')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Semua Halaman
        </button>

        <div className="rounded-xl border border-border p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2 text-primary">
              <LayoutTemplate className="h-5 w-5" />
            </div>
            <div>
              <h2 className="font-semibold">Halaman Profil Member</h2>
              <p className="text-sm text-muted-foreground">Belum diinisialisasi untuk tenant ini.</p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Inisialisasi halaman untuk mulai mengkustomisasi tampilan profil member
            menggunakan komponen block-based.
          </p>
          {createPage.error && (
            <p className="text-sm text-destructive">
              {(createPage.error as Error).message ?? 'Gagal membuat halaman.'}
            </p>
          )}
          <button
            onClick={() =>
              createPage.mutate({
                slug: 'member-profile',
                title: 'Profil Member',
                is_system: true,
                initialSections: DEFAULT_MEMBER_PROFILE_BLOCKS,
              })
            }
            disabled={createPage.isPending}
            className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createPage.isPending ? 'Menginisialisasi...' : 'Inisialisasi Halaman'}
          </button>
        </div>
      </div>
    )
  }

  if (!page) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Halaman tidak ditemukan.</p>
        <button
          onClick={() => navigate('/pages')}
          className="text-sm text-primary hover:underline"
        >
          Kembali ke daftar halaman
        </button>
      </div>
    )
  }

  const publicUrl = tenant?.slug
    ? `https://${tenant.slug}.oneidol.id/${slug === 'member-profile' ? 'members/[id]' : slug}`
    : `/${slug}`

  const pageScope = PAGE_SCOPE_MAP[slug]

  return (
    <div className="space-y-6">
      {/* Breadcrumb + preview link */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/pages')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Semua Halaman
        </button>
        {slug !== 'member-profile' && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Lihat Halaman
          </a>
        )}
      </div>

      <BlockEditor
        pageTitle={page.title}
        pageDescription={
          slug === 'member-profile'
            ? 'Template yang diterapkan ke semua halaman detail member.'
            : `URL: ${tenant?.slug ?? 'domain'}.oneidol.id/${slug}`
        }
        initialBlocks={page.sections}
        onSave={handleSave}
        isSaving={updateSections.isPending}
        saveError={updateSections.error as Error | null}
        saved={saved}
        listenToRouterState
        pageScope={pageScope}
      />
    </div>
  )
}
